import { useCallback, useEffect, useMemo } from 'react';

import { YrkesDocument, YrkesQuery, YrkesQueryVariables, Yrke } from 'api/yrke';
import { useLazyQueryInterval } from 'hooks/useLazyQueryInterval';
import { Names } from 'types/icon';
import { resource } from 'utils/resource/resource';

const DEFAULT_YRKE_ICON: Names = 'ellipsis';

type UseYrkesOptions = {
  /**
   * When true, only active yrkes are returned in `options`. Labels still
   * include inactive.
   */
  activeOnly?: boolean;
  skip?: boolean;
};

export type YrkeOption = {
  label: string;
  value: string;
};

/**
 * Loads system-wide Yrke values for selects, filters, and display labels.
 * When activeOnly is true, the API request includes active: true and options
 * only contain active yrkes. When activeOnly is false, all yrkes are loaded
 * (used for admin views and historical labels).
 */
export const useYrkes = ({
  activeOnly = true,
  skip = false,
}: UseYrkesOptions = {}) => {
  const [fetchYrkes, { data, loading, error }] = useLazyQueryInterval<
    YrkesQuery,
    YrkesQueryVariables
  >({
    document: YrkesDocument,
    name: 'yrkes',
    singlePageLimit: 200,
  });

  useEffect(() => {
    if (skip) {
      return;
    }
    fetchYrkes({
      variables: {
        filter: {
          limit: 200,
          ...(activeOnly ? { active: true } : {}),
        },
        sorting: {
          sorting: [{ field: 'displayName', direction: 'asc' }],
        },
      },
    });
  }, [activeOnly, fetchYrkes, skip]);

  const yrkes: Yrke[] = data?.yrkes.edges ?? [];

  const labels = useMemo(() => {
    const map = new Map<string, string>();
    yrkes.forEach(({ code, displayName }) => {
      map.set(code, displayName);
    });
    return map;
  }, [yrkes]);

  const getLabel = useCallback(
    (code?: string | null): string => {
      if (!code) {
        return '';
      }
      return labels.get(code) || code;
    },
    [labels]
  );

  const getIcon = useCallback((code?: string | null): Names => {
    if (!code) {
      return DEFAULT_YRKE_ICON;
    }
    return resource.getYrkeIcon(code);
  }, []);

  const options: YrkeOption[] = useMemo(() => {
    const list = activeOnly ? yrkes.filter((y) => y.active) : yrkes;
    return list.map(({ code, displayName }) => ({
      label: displayName,
      value: code,
    }));
  }, [activeOnly, yrkes]);

  return {
    yrkes,
    options,
    labels,
    getLabel,
    getIcon,
    loading,
    error,
  };
};
